<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveSlot">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('CMS-Slot bearbeiten') : $t('CMS-Slot anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Slot speichern') }}</span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button
                                    v-if="showDeleteButton"
                                    class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                    type="button"
                                    :disabled="slot.is_in_use"
                                    @click.stop="runHeaderAction('delete')"
                                >
                                    {{ $t('Slot löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Slot anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <app-panel :title="$t('Allgemein')">
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                <input v-model="slot.name" class="stage-filter-input form-input block w-full text-sm" required>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Key') }}</label>
                                <input v-model="slot.key" class="stage-filter-input form-input block w-full text-sm" :disabled="slot.is_system" required>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Scope') }}</label>
                                <select v-model="slot.scope" class="stage-filter-input form-input block w-full text-sm">
                                    <option v-for="scope in scopes" :key="scope.key" :value="scope.key">{{ scope.name }}</option>
                                </select>
                            </div>
                            <div>
                                <label class="stage-filter-label block mb-2">{{ $t('Maximale Module') }}</label>
                                <input v-model.number="slot.max_items" type="number" min="1" class="stage-filter-input form-input block w-full text-sm" :placeholder="$t('Unbegrenzt')">
                            </div>
                        </div>

                        <div class="mt-6">
                            <label class="stage-filter-label block mb-2">{{ $t('Beschreibung') }}</label>
                            <textarea v-model="slot.description" rows="3" class="stage-filter-input form-input block w-full text-sm"></textarea>
                        </div>
                </app-panel>

                <app-panel :title="$t('Zulässige Module')" panel-class="mt-6">
                        <div class="space-y-4">
                            <label v-for="type in moduleTypes" :key="type.id" class="flex items-center gap-3 text-sm text-gray-700">
                                <input
                                    :checked="slot.allowed_module_type_ids.includes(type.id)"
                                    type="checkbox"
                                    @change="toggleModuleType(type.id)"
                                >
                                <span>{{ type.name }}</span>
                            </label>
                        </div>
                    <template #footer>
                        <div class="text-sm text-gray-500">
                            {{ $t('Änderungen an der Modulauswahl gelten nur für zukünftige Zuordnungen. Bereits auf Seiten platzierte Module bleiben unverändert.') }}
                        </div>
                    </template>
                </app-panel>

                <app-panel v-if="isEdit" :title="$t('Nutzung')" panel-class="mt-6">
                        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                                <div class="stage-filter-label block mb-2">{{ $t('Gesamt') }}</div>
                                <div class="text-base text-gray-900">{{ slot.usage_count || 0 }}</div>
                            </div>
                            <div>
                                <div class="stage-filter-label block mb-2">{{ $t('Referenzen') }}</div>
                                <div class="text-base text-gray-900">{{ slot.reference_count || 0 }}</div>
                            </div>
                            <div>
                                <div class="stage-filter-label block mb-2">{{ $t('Module') }}</div>
                                <div class="text-base text-gray-900">{{ slot.placement_count || 0 }}</div>
                            </div>
                        </div>
                        <div v-if="slot.is_in_use" class="mt-4 text-sm text-amber-700">
                            {{ $t('Der Slot kann erst gelöscht werden, wenn alle Referenzen und Modul-Zuordnungen entfernt wurden.') }}
                        </div>
                        <div v-else-if="slot.is_system" class="mt-4 text-sm text-gray-500">
                            {{ $t('Dieser System-Slot kann nicht gelöscht werden.') }}
                        </div>
                </app-panel>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-slot-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </main>
</template>

<script>
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: 'cms-slot-editor',
    components: {ConfirmDialog},
    data() {
        return {
            loading: false,
            moduleTypes: [],
            scopes: [],
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            headerActionMenuOpen: false,
            slot: {
                key: '',
                name: '',
                description: '',
                scope: 'both',
                max_items: null,
                is_system: false,
                allowed_module_type_ids: [],
                usage_count: 0,
                reference_count: 0,
                placement_count: 0,
                is_in_use: false,
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        showDeleteButton() {
            return this.isEdit && !this.loading && !this.slot.is_system;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        this.loadOptions();
        if (this.isEdit) {
            this.getSlot();
        }
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/slots');
                return;
            }

            if (action === 'delete') {
                this.deleteSlot();
            }
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;

            if (!(menuElement && menuElement.contains(event.target))
                && !(triggerElement && triggerElement.contains(event.target))
                && !(wrapperElement && wrapperElement.contains(event.target))) {
                this.closeHeaderActionMenu();
            }
        },
        loadOptions() {
            axios.get('api/workspace/admin/cms/slots/options').then((response) => {
                this.moduleTypes = Array.isArray(response.data.module_types) ? response.data.module_types : [];
                const allowedIds = this.moduleTypes.map((type) => type.id);
                this.scopes = Array.isArray(response.data.scopes) ? response.data.scopes : [];
                if (!this.slot.allowed_module_type_ids.length) {
                    this.slot.allowed_module_type_ids = allowedIds;
                } else {
                    this.slot.allowed_module_type_ids = this.slot.allowed_module_type_ids.filter((id) => allowedIds.includes(id));
                }
            });
        },
        getSlot() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/slots/' + this.$route.params.id).then((response) => {
                this.slot = response.data;
                const allowedIds = this.moduleTypes.map((type) => type.id);
                if (allowedIds.length > 0) {
                    this.slot.allowed_module_type_ids = this.slot.allowed_module_type_ids.filter((id) => allowedIds.includes(id));
                }
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        toggleModuleType(typeId) {
            const exists = this.slot.allowed_module_type_ids.includes(typeId);
            if (exists) {
                this.slot.allowed_module_type_ids = this.slot.allowed_module_type_ids.filter((id) => id !== typeId);
            } else {
                this.slot.allowed_module_type_ids = [...this.slot.allowed_module_type_ids, typeId];
            }
        },
        saveSlot() {
            const payload = {
                ...this.slot,
                key: String(this.slot.key || '').trim(),
                name: String(this.slot.name || '').trim(),
                description: String(this.slot.description || '').trim(),
                max_items: this.slot.max_items ? Number(this.slot.max_items) : null,
            };

            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/slots/' + this.$route.params.id, payload)
                : axios.post('api/workspace/admin/cms/slots', payload);

            request.then((response) => {
                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Data updated correctly') : this.$t('Data saved correctly'), type: 'success'});
                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/slots/' + response.data.slot.id + '/edit');
                } else {
                    this.slot = response.data.slot;
                }
            });
        },
        deleteSlot() {
            this.openConfirmDialog({
                title: this.$t('Slot löschen'),
                text: this.$t('Soll dieser Slot wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/slots/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/slots');
                    });
                },
            });
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>
